import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { Stepper } from "@mantine/core";
import { gql } from "@/__generated__";
import { Button } from "@/components/buttons/button/button";
import styles from "./refer-patient-progress-tracker.module.scss";

export const GET_APPEAL_FOR_PROGRESS_TRACKER = gql(`
  query GetAppealForProgressTracker($appealId: ID!) {
    appealStatus(appealId: $appealId) {
      referred
    }
    appealPatient(appealId: $appealId) {
      firstName
      lastName
      dateOfBirth
    }
  }
`);

export const SEND_REFERRAL_TO_PATIENT = gql(`
  mutation SendReferralToPatient($appealId: ID!) {
    sendReferralToPatient(appealId: $appealId)
  }
`);

interface ReferPatientProgressTrackerProps {
  appealId?: string;
  onBeforeSendReferral?: () => Promise<boolean>;
}

function getActiveStep(pathname: string): number {
  if (pathname.startsWith("/appeals/") && pathname.includes("/strengthen")) {
    return 2;
  }

  if (pathname.startsWith("/refer/") && pathname.endsWith("/patient-info")) {
    return 1;
  }

  if (pathname === "/refer") {
    return 0;
  }

  return 0;
}

const STEPS = [
  { label: "Denial Info" },
  { label: "Patient Info" },
  { label: "Strengthen Referral" },
] as const;

export function ReferPatientProgressTracker({
  appealId,
  onBeforeSendReferral,
}: ReferPatientProgressTrackerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeStep = getActiveStep(location.pathname);

  const { data, loading } = useQuery(GET_APPEAL_FOR_PROGRESS_TRACKER, {
    variables: { appealId: appealId || "" },
    skip: !appealId,
    fetchPolicy: "cache-and-network",
  });

  const [sendReferralToPatient, { loading: mutationLoading }] = useMutation(
    SEND_REFERRAL_TO_PATIENT,
  );

  if (appealId && loading && !data) {
    return null;
  }

  const patient = data?.appealPatient;
  const hasPatientInfo = !!(
    patient?.firstName &&
    patient?.lastName &&
    patient?.dateOfBirth
  );
  const isReferred = data?.appealStatus?.referred || false;

  const shouldShowSendButton = appealId && hasPatientInfo && !isReferred;
  const isSendButtonDisabled = loading || mutationLoading;

  const handleSendReferral = async () => {
    if (!appealId) return;

    if (onBeforeSendReferral) {
      const canProceed = await onBeforeSendReferral();
      if (!canProceed) {
        return;
      }
    }

    await sendReferralToPatient({
      variables: { appealId },
    });
    navigate("/", {
      state: {
        success: true,
      },
    });
  };

  const StepIcon = ({ step }: { step: number }) => <span>{step}</span>;

  const title = isReferred ? "Appeal detail" : "Refer patient";
  const activeLabel = STEPS[activeStep]?.label ?? STEPS[0].label;

  return (
    <div className={styles.container}>
      <div className={styles.leftSection}>
        <div className={styles.iconContainer} />
        <h1 className={styles.title}>{title}</h1>
        {!isReferred && (
          <>
            <Stepper
              active={activeStep}
              orientation="horizontal"
              classNames={{
                root: styles.stepper,
                step: styles.step,
                stepIcon: styles.stepIcon,
                stepLabel: styles.stepLabel,
                separator: styles.separator,
              }}
              iconSize={32}
            >
              {STEPS.map(({ label }, index) => (
                <Stepper.Step
                  key={label}
                  label={label}
                  icon={<StepIcon step={index + 1} />}
                  completedIcon={<StepIcon step={index + 1} />}
                />
              ))}
            </Stepper>
            <span className={styles.compactIndicator}>
              Step {activeStep + 1} of {STEPS.length}: {activeLabel}
            </span>
          </>
        )}
      </div>

      {shouldShowSendButton && (
        <div className={styles.buttonContainer}>
          <Button
            variant="primary"
            size="small"
            onClick={handleSendReferral}
            disabled={isSendButtonDisabled}
            loading={mutationLoading}
          >
            Send Referral
          </Button>
        </div>
      )}
    </div>
  );
}
